import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ToastService } from '@peca/commons';

import { SettingsService } from '../../../services/settings.service';
import { TSetting } from '../../../types/settings.type';

type OptimizerSettings = {
  fixedCostPerSeller?: number | null;
  maxDiffCostPerProduct?: number | null;
  maxDiffCostPerProductPercentage?: number | null;
  costPerKm?: number | null;
};

type OptimizerSettingsForm = {
  fixedCostPerSeller?: string | number | null;
  maxDiffCostPerProduct?: string | number | null;
  maxDiffCostPerProductPercentage?: string | number | null;
  costPerKm?: string | number | null;
};

type SettingVersion = TSetting<OptimizerSettings>;

@Component({
  selector: 'peca-optimizer-settings',
  templateUrl: './optimizer-settings.component.html',
  styleUrls: ['./optimizer-settings.component.scss'],
  standalone: false,
})
export class OptimizerSettingsComponent implements OnInit {
  private readonly SETTING_KEY = 'optimizer';
  private settingsService = inject(SettingsService);
  private toast = inject(ToastService);
  private datePipe = new DatePipe('pt-BR');

  versions: number[] = [];

  settingFormGroup = new FormGroup({
    version: new FormControl<number | null>(null),
    selectVersion: new FormControl<number | null>(null),
    updatedBy: new FormControl<string | null>(null),
    updatedAt: new FormControl<string | null>(null),
    fixedCostPerSeller: new FormControl<number | null>(null),
    maxDiffCostType: new FormControl<'currency' | 'percentage'>('currency'),
    maxDiffCostPerProduct: new FormControl<number | null>(null),
    maxDiffCostPerProductPercentage: new FormControl<number | null>(null),
    costPerKm: new FormControl<number | null>(null),
  });

  ngOnInit(): void {
    this.fetchSettings();
    this.settingFormGroup.get('version')?.disable();
    this.settingFormGroup.get('updatedBy')?.disable();
    this.settingFormGroup.get('updatedAt')?.disable();

    this.settingFormGroup.get('maxDiffCostType')?.valueChanges.subscribe((type) => {
      if (type === 'currency') {
        this.settingFormGroup.patchValue({ maxDiffCostPerProductPercentage: null });
      } else {
        this.settingFormGroup.patchValue({ maxDiffCostPerProduct: null });
      }
    });

    this.settingFormGroup.get('selectVersion')?.valueChanges.subscribe((v) => {
      if (v != null) {
        this.loadSpecificVersion(v);
      }
    });
  }

  fetchSettings(): void {
    this.settingsService.getLastSetting<SettingVersion>(this.SETTING_KEY).subscribe({
      next: (s) => {
        const hasPercentage = s.maxDiffCostPerProductPercentage != null;
        this.settingFormGroup.setValue({
          version: s.version ?? null,
          selectVersion: s.version ?? null,
          updatedBy: s.createdBy ?? null,
          updatedAt: this.datePipe.transform(s.createdAt, 'dd/MM/yyyy HH:mm') ?? null,
          fixedCostPerSeller: s.fixedCostPerSeller ?? null,
          maxDiffCostType: hasPercentage ? 'percentage' : 'currency',
          maxDiffCostPerProduct: s.maxDiffCostPerProduct ?? null,
          maxDiffCostPerProductPercentage: s.maxDiffCostPerProductPercentage ?? null,
          costPerKm: s.costPerKm ?? null,
        });

        const current = s.version ?? 0;
        this.versions = Array.from({ length: current }, (_, i) => current - i);
      },
      error: () => {
        this.toast.failure('Falha ao carregar configurações do otimizador');
      },
    });
  }

  onSave(): void {
    const formValue = this.settingFormGroup.value;
    const sanitizedValue = this.sanitizeFormValue(formValue);
    this.settingsService.updateSetting<OptimizerSettings>(this.SETTING_KEY, sanitizedValue).subscribe({
      next: () => {
        this.toast.success('Configurações salvas');
        this.fetchSettings();
      },
      error: () => {
        this.toast.failure('Falha ao salvar configurações');
      },
    });
  }

  onReset(): void {
    this.fetchSettings();
  }

  private loadSpecificVersion(version: number): void {
    this.settingsService.getSettingVersion<SettingVersion>(this.SETTING_KEY, version).subscribe({
      next: (s) => {
        const hasPercentage = s.maxDiffCostPerProductPercentage != null;
        this.settingFormGroup.patchValue({
          version: s.version ?? null,
          updatedBy: s.createdBy ?? null,
          updatedAt: this.datePipe.transform(s.createdAt, 'dd/MM/yyyy HH:mm') ?? null,
          fixedCostPerSeller: s.fixedCostPerSeller ?? null,
          maxDiffCostType: hasPercentage ? 'percentage' : 'currency',
          maxDiffCostPerProduct: s.maxDiffCostPerProduct ?? null,
          maxDiffCostPerProductPercentage: s.maxDiffCostPerProductPercentage ?? null,
          costPerKm: s.costPerKm ?? null,
        });
      },
      error: () => {
        this.toast.failure('Falha ao carregar versão ' + version);
      },
    });
  }

  private sanitizeFormValue(value: Partial<OptimizerSettingsForm> & { maxDiffCostType?: string | null }): OptimizerSettings {
    const isCurrency = value.maxDiffCostType === 'currency';
    return {
      fixedCostPerSeller: this.sanitizeDecimal(value.fixedCostPerSeller),
      maxDiffCostPerProduct: isCurrency ? this.sanitizeDecimal(value.maxDiffCostPerProduct) : null,
      maxDiffCostPerProductPercentage: !isCurrency ? this.sanitizeDecimal(value.maxDiffCostPerProductPercentage) : null,
      costPerKm: this.sanitizeDecimal(value.costPerKm),
    };
  }

  private sanitizeDecimal(value: string | number | null | undefined): number | null {
    if (!value) return null;
    if (typeof value === 'number') return value;

    const cleaned = value.toString().replace(/[R$\s%]/g, '');
    const normalized = cleaned.replace(',', '.');
    const result = parseFloat(normalized);

    return isNaN(result) ? null : result;
  }
}
